import { Link, NavLink } from "react-router-dom";
import { Shield, BookOpen, Code, Plug, Terminal, Key, Menu, X, AlertTriangle, Lock, Building2, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useState } from "react";

const docNavItems = [
  { label: "Overview", href: "/docs", icon: BookOpen, end: true },
  { label: "Integrations", href: "/docs/guides/integrations", icon: Plug },
  { label: "Agent Setup", href: "/docs/guides/agent-setup", icon: Terminal },
  { label: "API Keys", href: "/docs/guides/api-keys", icon: Key },
  { label: "Alerts & Response", href: "/docs/guides/alerts-response", icon: AlertTriangle },
  { label: "Search & Investigation", href: "/docs/guides/search-investigation", icon: Search },
  { label: "Account & Security", href: "/docs/guides/account-security", icon: Shield },
  { label: "Data & Privacy", href: "/docs/guides/data-privacy", icon: Lock },
  { label: "MSP Guide", href: "/docs/guides/msp", icon: Building2 },
  { label: "API Reference", href: "/docs/api", icon: Code },
];

interface DocsLayoutProps {
  children: React.ReactNode;
}

export function DocsLayout({ children }: DocsLayoutProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav — matches landing page style */}
      <nav
        className="border-b sticky top-0 bg-background/70 backdrop-blur-xl z-10"
        aria-label="Documentation navigation"
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5">
              <img src="/logo.png" alt="" aria-hidden className="h-7 w-auto" />
              <span className="font-semibold text-lg tracking-tight">prayingmantis</span>
            </Link>
            <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-[0.14em] border border-border rounded-full px-2.5 py-0.5">
              Docs
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 text-muted-foreground hover:text-foreground"
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}
            >
              {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            {isAuthenticated ? (
              <Link
                to="/app"
                className="hidden sm:inline bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Back to app
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline text-muted-foreground hover:text-foreground text-sm">
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="hidden sm:inline bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Start Free Trial
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 flex">
        {/* Sidebar — desktop */}
        <aside className="hidden lg:block w-56 shrink-0 py-8 pr-8 border-r">
          <DocsSidebar />
        </aside>

        {/* Sidebar — mobile */}
        {mobileNavOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <button
              type="button"
              aria-label="Close documentation navigation"
              className="absolute inset-0 bg-background/80 backdrop-blur-sm"
              onClick={() => setMobileNavOpen(false)}
            />
            <aside
              className="absolute left-0 top-0 h-full w-64 bg-background border-r shadow-lg p-6 pt-20"
            >
              <DocsSidebar onNavigate={() => setMobileNavOpen(false)} />
            </aside>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 py-8 lg:pl-8">
          {children}
        </main>
      </div>
    </div>
  );
}

function DocsSidebar({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="flex flex-col gap-1">
      {docNavItems.map((item) => (
        <NavLink
          key={item.href}
          to={item.href}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )
          }
        >
          <item.icon className="h-4 w-4 shrink-0" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
